import { Button } from "@/atoms-react/button/Button.react";
import { Checkbox } from "@/atoms-react/input-checkbox/index.react";
import InputLabel from "@/atoms-react/input/Input.react";
import { Modal, ModalContent, type ModalProps } from "@/atoms-react/modal/index.react";
import { Base } from "@/atoms-react/text/Text.react";
import { useKypay } from "@/features/kypay/hooks/use-kypay.ts";
import type { WalletData } from "@/features/kypay/types";
import { Themes } from "@/types/theme";
import { SolidAuthnModal } from "@/views/identity/components/security/solid-authn-modal.react";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import WalletConnect from "../Wallet-connect/index.react";
import { useLogin, usePrivy } from "@privy-io/react-auth";
import config from "@/config";
import Loader from "@/atoms-react/loader/Loader.react";
import { useDispatch } from "react-redux";
import { setConfirmationCreateWallet } from "@/redux/slices/transfertActionSlice";

type ModalAddWalletProps = {
  wallet?: WalletData;
  isWalletLoading?: boolean;
  onValidated: () => void;
  userEmail?: string;
  address?: string;
  walletData: WalletData | undefined;
  isTokenyWalletFailed?: boolean;
} & ModalProps;

export const ModalAddWallet = ({
  wallet,
  isWalletLoading,
  onValidated = () => {},
  address,
  userEmail,
  walletData,
  isTokenyWalletFailed,
  ...props
}: ModalAddWalletProps) => {
  const { t } = useTranslation(["wallet"]);
  const [_wallet, _setWallet] = useState<string | null>("");
  const [agrees, setAgrees] = useState<boolean>(false);
  const [actionData, setActionData] = useState<string | null>(null);
  const [displayAuthenticationModal, setDisplayAuthenticationModal] = useState<boolean>(false);
  const [addExistingWallet, setAddExistingWallet] = useState<boolean>(false);
  const { createUserWalletMutation } = useKypay();
  const { mutateAsync: createUserWallet, isPending } = createUserWalletMutation();
  const { logout, authenticated, ready, user } = usePrivy();
  const [timedOut, setTimedOut] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!ready) setTimedOut(true);
    }, 3000);

    return () => clearTimeout(timer);
  }, [ready]);

  const dispatch = useDispatch();
  const { login } = useLogin({
    onComplete: ({ user }) => {
      if (!walletData && !walletData && !isTokenyWalletFailed && !!user)
        dispatch(setConfirmationCreateWallet(true));
    }
  });
  const handleLoginPrivy = async () => {
    if (ready && authenticated && !walletData && !isTokenyWalletFailed && user?.wallet) {
      dispatch(setConfirmationCreateWallet(true));
    } else if (ready) login();
    return;
  };
  const execute = async () => {
    handleCloseAuthModal();
    if (!_wallet) {
      toast.error(t("kypay.modal_add_wallet.error.must_enter_wallet"));
      return;
    }
    if (!agrees) {
      toast.error(t("kypay.modal_add_wallet.error.must_accept_condition"));
      return;
    }
    const response = await createUserWallet({
      olky_wallet: false,
      wallet_connect: false,
      walletAddress: _wallet
    });
    if (response.success) {
      props.onClose?.();
      toast.success(t("kypay.success.create_wallet"));
      onValidated();
    } else {
      await handleLogoutPrivy();
      toast.error(t("kypay.error.create_wallet"));
    }
  };
  useEffect(() => {
    if (props.open) {
      setAddExistingWallet(false);
    }
  }, [props.open]);

  const handleCloseAuthModal = () => {
    setDisplayAuthenticationModal(false);
  };

  const handleLogoutPrivy = async () => {
    if (authenticated) await logout();
  };
  if (!ready && !timedOut) {
    return <Loader />;
  }
  return !ready && timedOut ? (
    <></>
  ) : (
    <>
      <Modal
        onClose={async () => {
          await handleLogoutPrivy();
        }}
        className="w-full w-full max-w-xl"
        {...props}
      >
        {!ready ? (
          <Loader />
        ) : !addExistingWallet ? (
          <ModalContent title={t("kypay.modal_add_wallet.label.title")}>
            <Base className="mt-2 text-sm">{t("kypay.modal_add_wallet.label.description")}</Base>
            <WalletConnect
              disable={!config.USE_PRIVY && !ready}
              onClick={async () => {
                if (ready) await handleLoginPrivy();
                props.onClose?.();
              }}
            />

            <div>
              <Button
                size="lg"
                theme={Themes.white}
                className="mt-4 mb-8 w-full justify-start rounded-md p-8 text-black"
                onClick={async () => {
                  if (authenticated && !wallet) await logout();
                  setAddExistingWallet(true);
                }}
              >
                {t("kypay.modal_add_wallet.label.button_set_address")}
              </Button>
            </div>
          </ModalContent>
        ) : (
          <ModalContent title={t("kypay.modal_add_wallet.label.title")}>
            <div className={"mt-4 opacity-1"}>
              <div className={"mt-4 mb-6 w-full"}>
                <InputLabel
                  autoFocus={true}
                  label={t("kypay.modal_add_wallet.label.new_wallet")}
                  value={_wallet ?? ""}
                  onChange={(value) => _setWallet(value)}
                />
              </div>

              <Checkbox value={agrees} onChange={(value) => setAgrees(value)}>
                <Base className="mb-6 cursor-pointer">
                  {t("kypay.modal_add_wallet.label.checkbox_agree_correct_wallet")}
                </Base>
              </Checkbox>

              {!walletData && !isTokenyWalletFailed && (
                <Button
                  theme={Themes.primary}
                  disabled={!agrees || !_wallet}
                  loading={isPending}
                  onClick={() => {
                    setActionData(JSON.stringify({ wallet: _wallet }));
                    setDisplayAuthenticationModal(true);
                  }}
                >
                  {t("button.continue")}
                </Button>
              )}
            </div>
          </ModalContent>
        )}
      </Modal>
      {displayAuthenticationModal && (
        <SolidAuthnModal
          action="new_wallet"
          actionData={actionData ?? undefined}
          onClose={handleCloseAuthModal}
          onValidated={async () => {
            execute();
          }}
        />
      )}
    </>
  );
};
